import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { appConfig } from '../../config/appConfig';
import { API_CONFIG } from '../../config/api';
import { SSO_CALLBACK_STORAGE_KEY } from '../../pages/LoginCallback';
import { BarChart3, Eye, EyeOff, Lock, ShieldCheck, Users } from 'lucide-react';
import loginBackgroundUrl from '../../../assets/login-background.jpg';

type SsoCallbackResponse = {
  token: string;
  id: string;
  name: string;
  email: string;
  role: string;
  department: string | null;
  refreshToken: string;
  employeeId: string | null;
};

type BackendErrorPayload = {
  detail?: string;
  message?: string;
  error?: string;
};

const getErrorMessage = (err: unknown): string | undefined => {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    return typeof message === 'string' ? message : undefined;
  }
  return undefined;
};

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginWithSSO, completeSSOLogin, user, isLoading: authLoading } = useAuth();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!authLoading && user) {
      console.log('✅ User already authenticated, redirecting to dashboard');
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [ssoCompleting, setSsoCompleting] = useState(false);

  // One-shot: if /callback stashed OAuth params, finish the exchange here
  // while showing a full-page loader. sessionStorage is cleared immediately
  // so a refresh does not re-trigger with the same code.
  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(SSO_CALLBACK_STORAGE_KEY);
      if (raw) sessionStorage.removeItem(SSO_CALLBACK_STORAGE_KEY);
    } catch {
      return;
    }
    if (!raw) return;

    let params: Record<string, string> = {};
    try {
      params = JSON.parse(raw) as Record<string, string>;
    } catch {
      return;
    }
    const body: Record<string, string> = {};
    if (params.code) body.code = params.code;
    if (params.state) body.state = params.state;
    if (params.error) body.error = params.error;
    if (params.error_description) body.error_description = params.error_description;
    if (Object.keys(body).length === 0) return;

    setSsoCompleting(true);
    setError('');

    (async () => {
      try {
        const resp = await fetch(`${API_CONFIG.BACKEND_BASE_URL}/auth/sso/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!resp.ok) {
          let payload: BackendErrorPayload = {};
          try {
            payload = (await resp.json()) as BackendErrorPayload;
          } catch {
            /* non-JSON body */
          }
          throw new Error(
            payload.detail || payload.message || payload.error || `sign-in failed (${resp.status})`
          );
        }
        const data = (await resp.json()) as SsoCallbackResponse;

        await completeSSOLogin(data);
        navigate('/', { replace: true });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'sign-in failed');
        setSsoCompleting(false);
      }
    })();
  }, [completeSSOLogin, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!credentials.email.trim() || !credentials.password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(credentials.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      await login(credentials.email, credentials.password);
      navigate('/');
    } catch (err: unknown) {
      console.log('Login error:', err); // Debug log
      
      // Handle specific error messages from backend
      const errorData = (err as { response?: { data?: BackendErrorPayload } }).response?.data;
      if (errorData?.message) {
        setError(errorData.message);
      } else if (errorData?.error) {
          // Map backend error types to user-friendly messages
          switch (errorData.error) {
          case 'User not found':
            setError('No account found with this email address. Please check your email or contact an administrator.');
            break;
          case 'Invalid password':
            setError('Incorrect password. Please check your password and try again.');
            break;
          case 'Authentication failed':
            setError('Unable to verify credentials. Please try again.');
            break;
          default:
            setError(errorData.error);
        }
      } else {
        const message = getErrorMessage(err);
        // Handle different error message formats
        if (message?.includes('401') || message?.includes('Unauthorized')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else if (message?.includes('403') || message?.includes('Forbidden')) {
          setError('Access denied. Please contact support if you believe this is an error.');
        } else if (message?.includes('network') || message?.includes('fetch')) {
          setError('Unable to connect to the server. Please check your internet connection and try again.');
        } else if (message) {
          setError(message);
        } else {
          setError('Login failed. Please try again.');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Full-page loader while finishing Microsoft SSO exchange
  if (ssoCompleting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-700 font-medium">Completing Microsoft sign-in…</p>
          <p className="text-slate-500 text-sm mt-1">Getting your SmartCHG session ready.</p>
        </div>
      </div>
    );
  }

  // Show loading while checking auth or redirecting
  if (authLoading || user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">{user ? 'Redirecting to dashboard...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="finance-v2-repo2-theme relative flex min-h-[100svh] items-center justify-center overflow-hidden bg-[#FAF7F1] px-4 py-5 text-[#1F1A20] sm:px-6 lg:px-8">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${loginBackgroundUrl})` }}
      />
      <div aria-hidden="true" className="absolute inset-0 bg-[#1F1A20]/20" />
      <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(180deg,rgba(250,247,241,0.46)_0%,rgba(250,247,241,0.22)_44%,rgba(60,28,57,0.28)_100%)]" />

      <div className="relative z-10 w-full max-w-[360px] rounded-[14px] border border-[#EDE6E1] bg-white/95 px-5 py-5 shadow-[0_6px_12px_rgba(31,26,32,.08),0_24px_48px_-12px_rgba(110,52,80,.28)] backdrop-blur-md sm:max-w-[372px] sm:px-6 sm:py-6">
        <div className="mb-5 text-center">
          <img
            src="/brand/chg-logo.png"
            alt={`${appConfig.ownerName} logo`}
            className="mx-auto mb-2 h-auto w-32 object-contain sm:w-36"
          />
          <h1 className="whitespace-nowrap font-display text-[1.35rem] font-semibold leading-tight text-[#1F1A20] sm:text-[1.5rem]">
            Welcome to <span className="text-[#6E3450]">{appConfig.appName}</span>
          </h1>
          <p className="mt-1 text-[11px] font-semibold text-[#7B6B72]">
            SMART INSIGHTS. STRONG IMPACT.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-[18px] w-[18px] text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2.5">
                <p className="text-xs font-medium text-red-600">{error}</p>
              </div>
            </div>
          </div>
        )}

        {showPasswordLogin && (
          <>
            <form onSubmit={handleSubmit} className="space-y-2.5">
              <div>
                <label className="mb-1 block text-xs font-medium text-[#3F3338]">
                  Email ID
                </label>
                <input
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  className="w-full rounded-[12px] border border-[#D9CFC4] bg-white px-3 py-2.5 text-[13px] text-[#1F1A20] shadow-[0_1px_0_rgba(31,26,32,.04)] transition-all placeholder:text-[#A8989F] focus:border-[#6E3450] focus:outline-none focus:ring-2 focus:ring-[#6E3450]/20"
                  placeholder="Enter your email"
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-[#3F3338]">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    className="w-full rounded-[12px] border border-[#D9CFC4] bg-white px-3 py-2.5 pr-10 text-[13px] text-[#1F1A20] shadow-[0_1px_0_rgba(31,26,32,.04)] transition-all placeholder:text-[#A8989F] focus:border-[#6E3450] focus:outline-none focus:ring-2 focus:ring-[#6E3450]/20"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-[#A8989F] transition-colors hover:text-[#6E3450] focus:outline-none focus:ring-2 focus:ring-[#6E3450]/30"
                  >
                    {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="mt-1 w-full rounded-[12px] bg-[#6E3450] px-4 py-2.5 text-[13px] font-semibold text-white shadow-[0_6px_12px_rgba(31,26,32,.08),0_24px_48px_-12px_rgba(110,52,80,.28)] transition-colors hover:bg-[#5A2C58] focus:outline-none focus:ring-2 focus:ring-[#6E3450]/30 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="my-3.5 flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-[#7B6B72]">
              <div className="h-px flex-1 bg-[#EDE5D8]"></div>
              <span>Or</span>
              <div className="h-px flex-1 bg-[#EDE5D8]"></div>
            </div>
          </>
        )}

        {/* Microsoft SSO Button */}
        <button
          type="button"
          onClick={async () => {
            try {
              setIsLoading(true);
              await loginWithSSO();
            } catch (err: unknown) {
              setError(getErrorMessage(err) || 'SSO login failed. Please try again.');
            } finally {
              setIsLoading(false);
            }
          }}
          disabled={isLoading}
          className={`${showPasswordLogin ? '' : 'mt-6 '}flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#6E3450] px-4 py-2.5 text-[13px] font-semibold text-white shadow-[0_6px_12px_rgba(31,26,32,.08),0_24px_48px_-12px_rgba(110,52,80,.28)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#5A2C58] hover:shadow-[0_10px_18px_rgba(31,26,32,.10),0_28px_52px_-18px_rgba(110,52,80,.34)] focus:outline-none focus:ring-2 focus:ring-[#6E3450]/30 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0`}
        >
          <svg className="h-4 w-4" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="23" height="23" rx="2" fill="#F3F3F3"/>
            <path d="M0 0H11.5V11.5H0V0Z" fill="#F35325"/>
            <path d="M11.5 0H23V11.5H11.5V0Z" fill="#81BC06"/>
            <path d="M0 11.5H11.5V23H0V11.5Z" fill="#05A6F0"/>
            <path d="M11.5 11.5H23V23H11.5V11.5Z" fill="#FFBA08"/>
          </svg>
          <span>Sign in with Microsoft</span>
        </button>

        <button
          type="button"
          onClick={() => {
            setError('');
            setShowPasswordLogin((isVisible) => !isVisible);
          }}
          className="mx-auto mt-6 flex items-center justify-center gap-1.5 text-[10.5px] font-medium text-[#8A7C82] transition-colors hover:text-[#6E3450] focus:outline-none"
          aria-expanded={showPasswordLogin}
        >
          <Lock className="h-3 w-3" />
          <span>{showPasswordLogin ? 'Hide password login' : 'Authorized users only'}</span>
        </button>
      </div>
    </div>
  );
};
